import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BrandMark from "@/components/BrandMark";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ArrowLeft } from "lucide-react";
import { BRAND_ADDRESS, BRAND_PHONE_DISPLAY, BRAND_WHATSAPP_DISPLAY, BRAND_NAME } from "@/lib/brand";

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-5 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <BrandMark size="md" />
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/" className="text-xs text-zinc-400 hover:text-[#FFD700] inline-flex items-center gap-1" data-testid="privacy-back">
              <ArrowLeft className="w-4 h-4" /> {t("common.back")}
            </Link>
          </div>
        </div>

        <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-2">
          {isEn ? "Legal" : "Informativa"}
        </p>
        <h1 className="font-display text-4xl font-bold tracking-tight">
          {isEn ? "Privacy & Cookie Policy" : "Privacy e Cookie Policy"}
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">
          {isEn ? "Last updated:" : "Ultimo aggiornamento:"} 06/2026
        </p>

        <div className="mt-8 space-y-6 text-zinc-300 text-sm leading-relaxed" data-testid="privacy-body">
          {isEn ? <EnglishContent /> : <ItalianContent />}
        </div>

        <div className="mt-12 border-t border-zinc-800 pt-6 text-xs text-zinc-500">
          {BRAND_NAME} · {BRAND_ADDRESS}<br />
          Tel {BRAND_PHONE_DISPLAY} · WhatsApp {BRAND_WHATSAPP_DISPLAY}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-xl font-bold text-zinc-100 mb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function ItalianContent() {
  return (
    <>
      <Section title="1. Titolare del trattamento">
        <p>Il titolare del trattamento dei dati personali è <strong>LUPI GIORGIO</strong> (ditta individuale, brand commerciale &quot;Rent Lupi&quot;), con sede in Marciana Marina, Isola d&apos;Elba (LI) 57033, Italia. P.IVA 00655790491 · C.F. LPUGRG62B14E931G · PEC lupigiorgio@pec.it.</p>
        <p>Contatti: telefono <strong>+39 0565 904134</strong> · WhatsApp <strong>+39 0565 904134</strong> · Email <strong>lupiservice@hotmail.it</strong> · PEC <strong>lupigiorgio@pec.it</strong>.</p>
      </Section>
      <Section title="2. Riferimenti normativi">
        <p>Il trattamento dei dati personali è effettuato in conformità a:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Regolamento (UE) 2016/679</strong> (GDPR — General Data Protection Regulation);</li>
          <li><strong>D.Lgs. 30 giugno 2003 n. 196</strong> (Codice in materia di protezione dei dati personali), come modificato dal D.Lgs. 101/2018;</li>
          <li>Provvedimenti e Linee guida del <strong>Garante per la Protezione dei Dati Personali</strong>.</li>
        </ul>
      </Section>
      <Section title="3. Dati raccolti">
        <p>Raccogliamo esclusivamente i dati personali strettamente necessari all&apos;erogazione del servizio di noleggio veicoli:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Anagrafica: nome, cognome, data e luogo di nascita, codice fiscale;</li>
          <li>Recapiti: email, numero di telefono cellulare, indirizzo di residenza;</li>
          <li>Documento d&apos;identità: tipologia e numero (con copia fronte/retro al ritiro del veicolo);</li>
          <li>Patente di guida: numero, data scadenza (con copia fronte/retro);</li>
          <li>Dati di pagamento: gestiti direttamente da Revolut Ltd, NON conservati sui nostri server;</li>
          <li>Dati contrattuali: prenotazioni, contratti firmati, verbali di consegna/riconsegna, foto del veicolo al momento di consegna/riconsegna.</li>
        </ul>
        <p>In fase di registrazione e di accettazione del contratto registriamo inoltre: data e ora, indirizzo IP, user-agent del browser, lingua scelta e versione del documento accettato. Tali dati costituiscono prova legale dell&apos;avvenuta accettazione (<em>audit trail</em>).</p>
      </Section>
      <Section title="4. Finalità del trattamento">
        <p>I dati personali sono trattati <strong>esclusivamente per finalità connesse all&apos;attività di Rent Lupi</strong>, ovvero:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Esecuzione del contratto di noleggio (base giuridica: art. 6 par. 1 lett. b GDPR);</li>
          <li>Adempimenti fiscali, contabili, assicurativi e di legge — incluso obbligo di identificazione del conducente (base giuridica: art. 6 par. 1 lett. c GDPR);</li>
          <li>Gestione di sinistri, contestazioni amministrative, multe, recupero del veicolo;</li>
          <li>Comunicazioni di servizio: conferma prenotazione, promemoria, ritiro/riconsegna.</li>
        </ul>
        <p className="text-amber-300/90 mt-2"><strong>Importante:</strong> i dati personali NON vengono mai utilizzati per finalità di marketing, profilazione, invio di newsletter o cessione a terzi. <strong>Tutti i dati sono trattati unicamente per lo svolgimento della nostra attività lavorativa di noleggio veicoli.</strong></p>
      </Section>
      <Section title="5. Cookie">
        <p>Rent Lupi utilizza <strong>esclusivamente cookie tecnici necessari al funzionamento del sito</strong> (sessione di login, preferenza lingua, carrello, scelta cookie). Tali cookie non richiedono consenso ai sensi del Provvedimento del Garante del 10 giugno 2021.</p>
        <p><strong>Non utilizziamo</strong>: cookie di profilazione, cookie di marketing, cookie di terze parti (Google Analytics, Facebook Pixel, ecc.), strumenti di tracciamento pubblicitario.</p>
      </Section>
      <Section title="6. Conservazione dei dati">
        <p>I dati personali sono conservati per il tempo strettamente necessario alle finalità di cui sopra:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Dati anagrafici e contrattuali: <strong>10 anni</strong> dalla cessazione del rapporto, come previsto dalla normativa fiscale italiana (art. 2220 c.c.);</li>
          <li>Documenti d&apos;identità e patenti: conservati per la durata del contratto e per i successivi 10 anni ai fini di prova in caso di contenzioso;</li>
          <li>Audit trail GDPR (IP, User-Agent, timestamp): 10 anni;</li>
          <li>Foto di consegna/riconsegna: 2 anni dalla restituzione del veicolo;</li>
          <li>Audit trail Telegram/cookie: 2 anni.</li>
        </ul>
      </Section>
      <Section title="7. Comunicazione a terzi">
        <p>I dati possono essere comunicati esclusivamente ai seguenti soggetti, in qualità di responsabili del trattamento o autonomi titolari, per scopi connessi alla nostra attività:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Revolut Ltd</strong> (Irlanda) — fornitore del servizio di pagamento Merchant API, conforme GDPR;</li>
          <li><strong>Commercialista incaricato</strong> — per adempimenti contabili e fiscali;</li>
          <li><strong>Compagnia assicurativa</strong> — in caso di sinistro o richiesta indennizzo;</li>
          <li><strong>Forze dell&apos;Ordine, Autorità Giudiziaria, Garante Privacy</strong> — solo su richiesta legalmente fondata.</li>
        </ul>
        <p className="text-amber-300/90 mt-2"><strong>Non vendiamo, non cediamo e non condividiamo</strong> dati personali con soggetti terzi per finalità di marketing o profilazione. Nessun trasferimento avviene al di fuori dello Spazio Economico Europeo (SEE).</p>
      </Section>
      <Section title="8. Diritti dell'interessato">
        <p>In qualsiasi momento puoi esercitare i seguenti diritti, ai sensi degli artt. 15-22 GDPR:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Accesso</strong> (art. 15): chiedere copia dei tuoi dati;</li>
          <li><strong>Rettifica</strong> (art. 16): correggere dati inesatti;</li>
          <li><strong>Cancellazione</strong> (art. 17, &quot;diritto all&apos;oblio&quot;): salvo obblighi di legge contabili e fiscali;</li>
          <li><strong>Limitazione</strong> (art. 18) del trattamento;</li>
          <li><strong>Portabilità</strong> (art. 20): ricevere i dati in formato strutturato;</li>
          <li><strong>Opposizione</strong> (art. 21);</li>
          <li><strong>Reclamo al Garante</strong>: hai diritto di proporre reclamo all&apos;Autorità Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] underline">www.garanteprivacy.it</a>).</li>
        </ul>
        <p>Per esercitare i diritti scrivici a <strong>lupiservice@hotmail.it</strong>, via PEC a <strong>lupigiorgio@pec.it</strong> o su WhatsApp <strong>+39 0565 904134</strong>. Risponderemo entro 30 giorni.</p>
      </Section>
      <Section title="9. Sicurezza">
        <p>Adottiamo misure tecniche e organizzative adeguate per proteggere i dati: cifratura HTTPS (TLS 1.3), hashing delle password con bcrypt, autenticazione a due fattori per gli account amministratore, backup giornalieri, accesso ai dati limitato al solo personale autorizzato.</p>
      </Section>
      <Section title="10. Modifiche">
        <p>La presente policy può essere aggiornata in qualsiasi momento. La versione attualmente in vigore è indicata in alto. Eventuali modifiche sostanziali saranno comunicate via email agli utenti registrati.</p>
      </Section>
    </>
  );
}

function EnglishContent() {
  return (
    <>
      <Section title="1. Data controller">
        <p>The data controller is <strong>LUPI GIORGIO</strong> (sole proprietorship, commercial brand &quot;Rent Lupi&quot;), located in Marciana Marina, Elba Island (LI) 57033, Italy. VAT 00655790491 · Tax Code LPUGRG62B14E931G · PEC lupigiorgio@pec.it.</p>
        <p>Contacts: phone <strong>+39 0565 904134</strong> · WhatsApp <strong>+39 0565 904134</strong> · Email <strong>lupiservice@hotmail.it</strong> · PEC <strong>lupigiorgio@pec.it</strong>.</p>
      </Section>
      <Section title="2. Legal framework">
        <p>Personal data processing complies with: <strong>EU Reg. 2016/679 (GDPR)</strong>, Italian <strong>Legislative Decree 196/2003</strong> (as amended by D.Lgs. 101/2018) and decisions of the Italian Data Protection Authority (Garante).</p>
      </Section>
      <Section title="3. Data we collect">
        <p>We collect only personal data strictly necessary to provide the rental service: full name, date and place of birth, fiscal code, email, mobile phone, address, ID document (front/back copy at vehicle pickup), driving licence (number and copy front/back), booking and contract details, signed contracts, delivery/return forms, vehicle photos.</p>
        <p>Payment data is processed directly by Revolut Ltd and <strong>never stored on our servers</strong>.</p>
        <p>For legal traceability, we also record IP address, browser user-agent, language and document version at the moment of registration and contract acceptance (audit trail).</p>
      </Section>
      <Section title="4. Purposes">
        <p>Data is processed <strong>solely for purposes related to Rent Lupi&apos;s business activity</strong>: rental contract execution; tax, accounting, insurance and legal obligations; claim handling and dispute resolution; service communications (booking confirmation, reminders, pickup/return).</p>
        <p className="text-amber-300/90 mt-2"><strong>Important:</strong> personal data is NEVER used for marketing, profiling, newsletters or any third-party sale. <strong>All data is processed exclusively for our rental business.</strong></p>
      </Section>
      <Section title="5. Cookies">
        <p>Rent Lupi only uses <strong>strictly necessary technical cookies</strong> (login session, language, cart, cookie choice). No profiling, marketing or third-party cookies (Google Analytics, Facebook Pixel, etc.) are used.</p>
      </Section>
      <Section title="6. Retention">
        <p>Personal data is kept only as long as needed and for the period required by Italian tax law: anagraphic and contractual data <strong>10 years</strong>; ID documents and driving licences during the contract and 10 years thereafter for evidence purposes; GDPR audit trail 10 years; pickup/return photos 2 years.</p>
      </Section>
      <Section title="7. Third-party disclosure">
        <p>Data may be disclosed only to: <strong>Revolut Ltd</strong> (Ireland, payment processor, GDPR-compliant); our accountant; insurance company in case of claim; competent authorities upon legally founded request.</p>
        <p className="text-amber-300/90 mt-2"><strong>We do not sell, transfer or share</strong> personal data with third parties for marketing or profiling. No transfer outside the European Economic Area (EEA).</p>
      </Section>
      <Section title="8. Your rights">
        <p>Under articles 15-22 GDPR you may exercise: access, rectification, erasure (subject to tax/accounting retention), restriction, portability, objection. You may also lodge a complaint with the Italian Data Protection Authority (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] underline">www.garanteprivacy.it</a>).</p>
        <p>To exercise your rights write to <strong>lupiservice@hotmail.it</strong>, certified PEC <strong>lupigiorgio@pec.it</strong> or WhatsApp <strong>+39 0565 904134</strong>. We respond within 30 days.</p>
      </Section>
      <Section title="9. Security">
        <p>We adopt adequate technical and organisational measures: HTTPS encryption (TLS 1.3), bcrypt password hashing, two-factor authentication for admin accounts, daily backups, restricted data access.</p>
      </Section>
      <Section title="10. Changes">
        <p>This policy may be updated. The current version date is displayed at the top. Substantial changes will be communicated by email to registered users.</p>
      </Section>
    </>
  );
}
